import React, {Component, ReactElement} from "react";
import {TextField} from "@material-ui/core";
import {defer} from "lodash";
import {changeHandler} from "../../../common/forms";

export interface TextInputProps {
  value?: string | null;
  disabled?: boolean;
  onChange?: (value: string) => void;
  validate?: (value: string) => Promise<string | null>;
  format?: (value: string) => string;
  autoFocus?: boolean;
}

interface TextInputState {
  value: string;
  valueError: boolean;
  valueHelperText: string;
}

/**
 * Common component for text input fields.
 */
export default class TextInput extends Component<
  TextInputProps,
  TextInputState
> {
  constructor(props: TextInputProps) {
    super(props);

    this.state = {
      value: props.value || "",
      valueError: false,
      valueHelperText: "",
    };
  }

  public get value(): string {
    return this.state.value;
  }

  onChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const {disabled, onChange} = this.props;
    if (disabled) {
      event.preventDefault();
      return;
    }

    changeHandler.call(this, event);
    defer(() => {
      if (onChange) {
        onChange(this.state.value);
      }
    });
  }

  async validate(): Promise<boolean> {
    const validate = this.props.validate;

    if (validate) {
      try {
        const error = await validate(this.state.value);

        if (error) {
          this.setState({
            valueError: true,
            valueHelperText: error,
          });
          return false;
        }
      } catch (error) {
        this.setState({
          valueError: true,
          valueHelperText: "Validation failed",
        });
        return false;
      }
    }

    return true;
  }

  onBlur(): void {
    const {format} = this.props;
    let value = this.state.value;

    if (format) {
      value = format(value);
    } else {
      value = value.trim();
    }

    if (value !== this.state.value) {
      this.setState({
        value: value,
      });
    }
    defer(() => {
      this.validate();
    });
  }

  render(): ReactElement {
    const {autoFocus, disabled} = this.props;
    const {value, valueError, valueHelperText} = this.state;

    return (
      <TextField
        variant="outlined"
        error={valueError}
        helperText={valueHelperText}
        name="value"
        value={value}
        autoComplete="off"
        onChange={this.onChange.bind(this)}
        onBlur={this.onBlur.bind(this)}
        disabled={disabled}
        autoFocus={autoFocus}
      />
    );
  }
}
